'use client';

import { userClient } from '@/lib/clients/user-client';
import { User } from '@repo/common/types';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface CurrentUserContextValue {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoadingUser: boolean;
}

export const CurrentUserContext = createContext<
  CurrentUserContextValue | undefined
>(undefined);

interface CurrentUserProviderProps {
  children: ReactNode;
}

export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
  const [user, setUser] = useState<User | null>(getCachedUser);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleSetUser = (user: User | null) => {
    cacheUser(user);
    setUser(user);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await userClient.currentUser();
        cacheUser(user);
        setUser(user);
      } catch (_err) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, []);

  return (
    <CurrentUserContext.Provider
      value={{ user, setUser: handleSetUser, isLoadingUser: isLoading }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }

  return context;
}

/**
 * Retrieves the cached user from localStorage.
 * Returns null if no valid item is found.
 */
function getCachedUser(): User | null {
  if (typeof window === 'undefined') return null;
  const cachedUserString = localStorage.getItem('currentUser');
  if (cachedUserString) {
    try {
      return JSON.parse(cachedUserString);
    } catch (error) {
      console.error('Error parsing cached user:', error);
    }
  }
  return null;
}

/**
 * Updates the cached user in localStorage.
 * If the user is null, the cache is cleared.
 */
function cacheUser(user: User | null) {
  if (typeof window === 'undefined') return;

  if (user) {
    localStorage.setItem('currentUser', JSON.stringify(user));
  } else {
    localStorage.removeItem('currentUser');
  }
}
